import { rolesQuery, useResetRoleMutation } from "@common/api/roles"
import { useUsers } from "@common/api/users"
import { ConfirmDialog } from "@common/components/shared/confirm-dialog"
import { useReAuthGuard } from "@common/hooks/use-reauth-guard"
import { handleError } from "@common/utils"
import { useSuspenseQuery } from "@tanstack/react-query"
import { createFileRoute, useNavigate } from "@tanstack/react-router"
import useCustomToast from "@/hooks/use-custom-toast"
import { memberCount } from "@/routes/_layout/settings/_panel/workspace/roles/-lib/member-count"

export const Route = createFileRoute(
  "/_layout/settings/_panel/workspace/roles/_list/$roleId/reset",
)({
  component: ResetRoleDialog,
})

function ResetRoleDialog(): React.ReactElement | null {
  const { roleId } = Route.useParams()
  const { data } = useSuspenseQuery(rolesQuery())
  const { totalCount, isLoading } = useUsers({ role_ids: [roleId] })
  const navigate = useNavigate()
  const showToast = useCustomToast()
  const resetMutation = useResetRoleMutation()
  const { guard, handleFreshnessError, reAuthModal } = useReAuthGuard()
  const role = data.data.find((item) => item.id === roleId)

  if (!role?.is_customized) {
    return null
  }

  const name = role.label
  const close = () =>
    navigate({
      to: "/settings/workspace/roles/$roleId",
      params: { roleId },
      search: true,
    })

  const send = () =>
    resetMutation.mutate(
      { role_id: roleId },
      {
        onSuccess: () => {
          showToast(
            "Reset",
            `${name} is back to its default permissions.`,
            "success",
          )
          close()
        },
        onError: (error) => {
          if (handleFreshnessError(error, send)) return
          handleError(error, showToast)
        },
      },
    )

  return (
    <>
      <ConfirmDialog
        title={`Reset ${name} to its default permissions?`}
        description={consequence(totalCount)}
        action="Reset"
        tone="default"
        pendingAction="Resetting…"
        pending={resetMutation.isPending}
        disabled={isLoading}
        onConfirm={() => guard(send)}
        onCancel={close}
      />
      {reAuthModal}
    </>
  )
}

const RESUMES_UPDATES =
  "Your changes are discarded and Fleetcraft keeps it up to date again."

function consequence(holders: number): string {
  return holders === 0
    ? `No one has this role. ${RESUMES_UPDATES}`
    : `${memberCount(holders)} with this role will get the default permissions. ${RESUMES_UPDATES}`
}
